import './styles/GroupingsList.scss';

import { useMutation, useQuery } from '@apollo/client';
import { Add } from '@mui/icons-material';
import { Button, Grid, Icon, Paper, Tooltip } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import DELETE_MUTATION from 'gql/mutations/ModulesService/deleteGrouping.gql';
import QUERY from 'gql/queries/ModulesService/getGroupings.gql';

import { TypedGridColumns } from 'app/components';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import { DataGrid } from 'app/components/DataGrid';
import ErrorBoundary from 'app/components/ErrorBoundary';
import GQLGuard from 'app/components/GQLGuard';
import { Modal } from 'app/components/Modal';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import usePageHeading from 'utils/hooks/usePageHeading';

import { Grouping } from '../../ModuleSelections/models';
import { GroupingModal } from '../GroupingModal';

export function GroupingsList() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingGrouping, setEditingGrouping] = useState<Grouping | null>(null);
  const [deletingGrouping, setDeletingGrouping] = useState<string | null>(null);
  const [disabledCancel, setDisabledCancel] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0
  });

  usePageHeading({
    heading: t(translations.pages.groupings.title()),
    mobilePadding: false,
    className: 'groupingsList'
  });

  const groupingsQuery = useQuery<
    any,
    {
      offset?: number;
      limit?: number;
    }
  >(QUERY, {
    context: { clientName: 'federation' },
    variables: { offset: 0, limit: paginationModel.pageSize }
  });
  const { refetch } = groupingsQuery;
  const [deleteGrouping] = useMutation(DELETE_MUTATION, { context: { clientName: 'federation' } });

  const handlePagination = useCallback(
    (event, page: number) => {
      const offset = page * paginationModel.pageSize;
      refetch({ offset: offset });
    },
    [paginationModel.pageSize, refetch]
  );

  const handlePaginationChange = useCallback(pagination => {
    setPaginationModel(pagination);
  }, []);

  const handleDelete = async id => {
    try {
      await deleteGrouping({
        variables: {
          id: parseInt(id, 10)
        },
        refetchQueries: ['GetGroupings']
      });
      dispatch(
        AlertActions.success(t(translations.pages.groupings.alerts.successDeletingGrouping()))
      );
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.groupings.alerts.errorDeletingGrouping())));
    }
  };

  const rowActions = useCallback(
    params => {
      return [
        <GridActionsCellItem
          icon={
            <Icon
              className="far fa-edit"
              style={{
                width: 'auto',
                color: 'lightblue'
              }}
            />
          }
          label={t(translations.common.edit())}
          onClick={() => setEditingGrouping(params.row)}
          showInMenu
        />,
        <GridActionsCellItem
          icon={
            <Icon
              className="far fa-trash-alt"
              style={{
                width: 'auto',
                color: 'red'
              }}
            />
          }
          label={t(translations.common.delete())}
          onClick={() => setDeletingGrouping(params.id)}
          showInMenu
        />
      ];
    },
    [t]
  );

  const columns = useMemo((): TypedGridColumns<Grouping> => {
    return [
      {
        flex: 2,
        minWidth: 200,
        headerName: t(translations.pages.groupings.modal.groupingName()),
        field: 'name',
        renderCell: params => (
          <Tooltip title={params.row.name}>
            <span>{params.row.name}</span>
          </Tooltip>
        )
      },
      {
        flex: 6,
        minWidth: 600,
        headerName: t(translations.pages.groupings.modal.description()),
        field: 'description',
        renderCell: params => (
          <Tooltip title={params.row.description}>
            <span>{params.row.description}</span>
          </Tooltip>
        )
      },
      {
        flex: 1,
        minWidth: 100,
        headerName: t(translations.common.actions()),
        field: 'actions',
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [t, rowActions]);

  const ControlButtons = () => {
    return (
      <Button
        startIcon={<Add />}
        variant="contained"
        className="users-management__actions--primary"
        onClick={() => {
          setCreateModalOpen(true);
        }}
      >
        {t(translations.common.create())}
      </Button>
    );
  };

  return (
    <>
      <ErrorBoundary boundaryTag="groupingsModal">
        <Modal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          headline={t(translations.pages.groupings.modal.createGroupingTitle())}
          disabledCancel={disabledCancel}
        >
          <GroupingModal
            closeHandler={() => setCreateModalOpen(false)}
            setDisabledCancel={setDisabledCancel}
          />
        </Modal>
      </ErrorBoundary>
      <ErrorBoundary boundaryTag="groupingsModal">
        <Modal
          open={!!editingGrouping}
          onClose={() => setEditingGrouping(null)}
          headline={t(translations.pages.groupings.modal.createGroupingTitle())}
          disabledCancel={disabledCancel}
        >
          <GroupingModal
            closeHandler={() => setEditingGrouping(null)}
            setDisabledCancel={setDisabledCancel}
            grouping={editingGrouping}
          />
        </Modal>
      </ErrorBoundary>
      <ConfirmationDialog
        open={!!deletingGrouping}
        title={t(translations.pages.groupings.deleteConfirm.title())}
        onClose={() => setDeletingGrouping(null)}
        onConfirm={() => {
          handleDelete(deletingGrouping);
          setDeletingGrouping(null);
        }}
      >
        <div>{t(translations.pages.groupings.deleteConfirm.message())}</div>
      </ConfirmationDialog>
      <ErrorBoundary boundaryTag="groupingsList">
        <GQLGuard
          query={groupingsQuery}
          onError={error => {
            if (error?.graphQLErrors[0]?.extensions?.status === 404) {
              dispatch(
                AlertActions.error(`${t(translations.pages.modules.alerts.failedLoading())}`)
              );
            } else {
              dispatch(
                AlertActions.error(
                  `${t(translations.pages.modules.alerts.failedLoading())}: ${error.message}`
                )
              );
            }
            navigate('/', { replace: true });
            return null;
          }}
        >
          {groupingsData => {
            return (
              <>
                <Grid
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    paddingBottom: '1em'
                  }}
                  container
                >
                  <ControlButtons />
                </Grid>
                <Grid container flexGrow={1} component={Paper} className="groupingsList__paper">
                  <DataGrid
                    disableColumnFilter
                    disableRowSelectionOnClick
                    columns={columns.map(column => ({
                      ...column,
                      sortable: false
                    }))}
                    rows={groupingsData.getGroupings}
                    paginationMode="server"
                    // onPageSizeChange={handlePageSizeChange}
                    // pageSize={pageSize}
                    rowCount={groupingsData.getGroupingsCount}
                    paginationModel={{
                      page: paginationModel.page,
                      pageSize: paginationModel.pageSize
                    }}
                    onPaginationModelChange={pagModel => {
                      handlePaginationChange(pagModel);
                      handlePagination(null, pagModel.page);
                    }}
                    // onPageChange={(page, details) => handlePagination(details, page)}
                  />
                </Grid>
              </>
            );
          }}
        </GQLGuard>
      </ErrorBoundary>
    </>
  );
}
